// @flow

import React, { type ComponentType } from 'react';
import { connect } from 'react-redux';
import type { State as ReduxState } from 'app/common/redux/reducers/types';
import type { VideosAutoplaying } from 'app/common/redux/reducers/ui/app';
import withVisibilityChanged from 'app/common/lib/react/withVisibilityChanged';
import { type Dispatch } from 'redux';
import { type Pin } from 'app/common/react/components/PinRep/types';
import PinVideo, {
  type OwnProps as PinVideoProps,
} from 'app/common/react/components/PinRep/components/PinVisual/PinVideo';
import {
  areAllOtherVideosPaused,
  getCurrentVideoTime,
  isVideoPlaying,
} from 'app/common/lib/util/PinUtils';
import {
  pauseAutoplay,
  setCurrentVideo,
  type SetVideoParams,
  type SetVideoPauseStateParams,
} from 'app/common/redux/actions/app';

type PinVideoObserverType = ComponentType<{|
  onVisibilityChanged: (visible: boolean) => void,
  trackFullVisible?: boolean,
  ...PinVideoProps,
|}>;
const PinVideoObserver: PinVideoObserverType = withVisibilityChanged(PinVideo);

type OwnProps = {|
  currentTimeFromPinRep?: ?number,
  // fallback props to create image for when video is not autoplaying
  highResOnly?: boolean,
  inAdsDesktopVideoExperiment?: boolean,
  // the endframe only works on Experimental PinRep
  // TODO: @crueb rename this variable once old PinRep is fully deprecated
  isExperimentalPinRep: boolean,
  lowResOnly?: boolean,
  onError?: () => void,
  onLoad?: () => void,
  onTimeChange?: ({ event: SyntheticEvent<HTMLVideoElement>, time: number }) => void,
  pin: Pin,
  rootMargin?: string,
  saved?: boolean,
  viewParameter?: $Values<PCONST.thrift.ViewParameterType>,
  viewType?: $Values<PCONST.thrift.ViewType>,
|};

type StateProps = {|
  appInFocus?: boolean,
  isAutoplay: boolean,
  videosAutoplaying: VideosAutoplaying,
|};

type DispatchProps = {|
  pauseAutoplay: (setVideoPauseState: SetVideoPauseStateParams) => void,
  setCurrentVideo: (setVideoParams: SetVideoParams) => void,
|};

type Props = {|
  ...OwnProps,
  ...StateProps,
  ...DispatchProps,
|};

export class PinVideoWithVisibility extends React.Component<Props> {
  currentVideoCurrentTime: ?number;

  // The purpose of this local variable is to keep the state of which individual videos
  // were affected by the browser focus. Not using state because this component updates
  // very frequently due to tracking video time (every ~0.5 secs). Not using redux store bc there is
  // is a timing issue between the store updating with the other video pins' state and playing this video.
  pausedDueToBrowserOutOfFocus: ?boolean;

  componentDidMount() {
    //send "loaded" callback to enable impressions, impression framework does own intersection handling for visibility
    if (this.props.onLoad) {
      this.props.onLoad();
    }
  }

  componentDidUpdate(prevProps: Props) {
    const { appInFocus: prevAppInFocus } = prevProps;
    const {
      appInFocus: currAppInFocus,
      currentTimeFromPinRep,
      pauseAutoplay,
      pin,
      setCurrentVideo,
      videosAutoplaying,
    } = this.props;
    const { id: pinId, is_promoted: isPromoted } = pin;

    const isCurrentlyPlaying = isVideoPlaying(pinId, videosAutoplaying);
    const currentVideoTime = getCurrentVideoTime(pinId, videosAutoplaying);

    // When the browser focus state changes and the it's no longer in focus and this
    // video is playing.
    if (currAppInFocus !== prevAppInFocus && !currAppInFocus && isCurrentlyPlaying) {
      this.pausedDueToBrowserOutOfFocus = true;
      pauseAutoplay({
        pinId,
        isPromoted,
        currentTime: currentTimeFromPinRep || this.currentVideoCurrentTime,
      });
      // When this video was paused due to the browser going out of focus and the app
      // is back in focus.
    } else if (this.pausedDueToBrowserOutOfFocus && currAppInFocus) {
      this.pausedDueToBrowserOutOfFocus = false;
      setCurrentVideo({
        pinId,
        isPromoted,
        currentTime: currentVideoTime || currentTimeFromPinRep || this.currentVideoCurrentTime,
      });
    }
  }

  componentWillUnmount() {
    const { currentTimeFromPinRep, pauseAutoplay, pin, videosAutoplaying } = this.props;
    const { id: pinId, is_promoted: isPromoted } = pin;

    const isCurrentlyPlaying = isVideoPlaying(pinId, videosAutoplaying);
    // Puts video in a pause state in redux when it unmounts. Allows this video to resume when
    // back in view.
    if (isCurrentlyPlaying) {
      pauseAutoplay({
        pinId,
        isPromoted,
        currentTime: currentTimeFromPinRep || this.currentVideoCurrentTime,
      });
    }
  }

  handleVisibilityChanged = (visible: boolean) => {
    const {
      currentTimeFromPinRep,
      pin,
      setCurrentVideo,
      pauseAutoplay,
      videosAutoplaying,
    } = this.props;
    const { id: pinId, is_promoted: isPromoted } = pin;
    const currentVideoTime = getCurrentVideoTime(pinId, videosAutoplaying);

    const isCurrentlyPlaying = isVideoPlaying(pinId, videosAutoplaying);
    // Check for whether all videos are paused. Organic videos are only allowed to autoplay when there are no videos
    // currently autoplaying (autoplay map is empty or all videos are paused).
    const otherVideosAllPaused = areAllOtherVideosPaused(pinId, videosAutoplaying);

    if (visible) {
      // We do not have a cap for how many promoted videos play at one time.
      // Whether there is an organic or promoted video already playing, we will begin autoplaying a newly visible
      // promoted video.
      if (isPromoted) {
        setCurrentVideo({
          pinId,
          isPromoted,
          // currentVideoTime is when a video was paused and has a currentTime in the store.
          // currentTimeFromPinRep is bc of a discrepancy between old PinRep and lego pinrep.
          // this.currentVideoCurrentTime is used to save the time of the currently playing video
          // to save in the store.
          currentTime: currentVideoTime || currentTimeFromPinRep || this.currentVideoCurrentTime,
        });
        // We only autoplay organic videos when there are no organic or promoted videos already autoplaying
      } else if (otherVideosAllPaused) {
        setCurrentVideo({
          pinId,
          isPromoted,
          // currentVideoTime is when a video was paused and has a currentTime in the store.
          // currentTimeFromPinRep is bc of a discrepancy between old PinRep and lego pinrep.
          // this.currentVideoCurrentTime is used to save the time of the currently playing video
          // to save in the store.
          currentTime: currentVideoTime || currentTimeFromPinRep || this.currentVideoCurrentTime,
        });
      }
    } else if (!visible && isCurrentlyPlaying) {
      // When a video pin goes out of view and was playing, we want to pause the video.
      // We need to use currentTimeFromPinRep || this.currentVideoCurrentTime bc of the PinRep and ExperimentalPinRep
      // discrepancies.
      pauseAutoplay({
        pinId,
        isPromoted,
        currentTime: currentTimeFromPinRep || this.currentVideoCurrentTime,
      });
    }
  };

  handleVideoTimeChange = (args: { event: SyntheticEvent<HTMLVideoElement>, time: number }) => {
    const { pin, onTimeChange, videosAutoplaying } = this.props;
    const { id: pinId } = pin;

    const isCurrentlyPlaying = isVideoPlaying(pinId, videosAutoplaying);
    if (isCurrentlyPlaying) {
      this.currentVideoCurrentTime = args.time;
    }

    // onTimeChange is passed from PinRep. This is needed bc of a discrepancy between PinRep and ExperimentalPinRep
    if (onTimeChange) {
      onTimeChange(args);
    }
  };

  render() {
    const {
      isAutoplay,
      pin,
      videosAutoplaying,
      viewParameter,
      viewType,
      highResOnly,
      lowResOnly,
      onError,
      onLoad,
      saved,
      inAdsDesktopVideoExperiment,
      isExperimentalPinRep,
      rootMargin,
    } = this.props;
    const { id } = pin;
    const currentVideoTime = getCurrentVideoTime(id, videosAutoplaying);

    // isAutoPlay represents the slight delay in App.js to improve PWT. Only changes on page load.
    const isCurrentlyPlaying = isVideoPlaying(id, videosAutoplaying);
    const shouldPlay = isCurrentlyPlaying && isAutoplay;

    return (
      <PinVideoObserver
        rootMargin={rootMargin}
        currentTime={currentVideoTime}
        inAdsDesktopVideoExperiment={inAdsDesktopVideoExperiment}
        isAutoplay={isAutoplay}
        viewParameter={viewParameter}
        viewType={viewType}
        onTimeChange={this.handleVideoTimeChange}
        onVisibilityChanged={this.handleVisibilityChanged}
        pin={pin}
        playing={shouldPlay}
        trackFullVisible={false}
        highResOnly={highResOnly}
        lowResOnly={lowResOnly}
        onError={onError}
        onLoad={onLoad}
        saved={saved}
        isExperimentalPinRep={isExperimentalPinRep}
      />
    );
  }
}

const mapStateToProps = (state: ReduxState, ownProps: OwnProps) => ({
  appInFocus: state.ui.app.appInFocus,
  isAutoplay: state.ui.app.isAutoplay,
  videosAutoplaying: state.ui.app
    ? state.ui.app.videosAutoplaying
    : {
        organicVideosAutoplaying: {},
        promotedVideosAutoplaying: {},
      },
});

const mapDispatchToProps = (dispatch: Dispatch<any>, ownProps: OwnProps) => ({
  pauseAutoplay: (setVideoPauseState: SetVideoPauseStateParams) =>
    dispatch(pauseAutoplay(setVideoPauseState)),
  setCurrentVideo: (setVideoParams: SetVideoParams) => dispatch(setCurrentVideo(setVideoParams)),
});

export default connect<Props, OwnProps, _, _, _, _>(
  mapStateToProps,
  mapDispatchToProps,
)(PinVideoWithVisibility);
